import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, Loader2, Receipt, UserRound, ExternalLink } from "lucide-react";
import { api, apiError, rupiah, tanggal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState } from "@/components/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KtpPreview } from "@/components/KtpPreview";

const PROFIL_FIELDS = [
  ["nama_lengkap", "Nama Lengkap"],
  ["email", "Email"],
  ["no_hp", "No HP"],
  ["sekuritas", "Sekuritas"],
  ["kode_nasabah", "Kode Nasabah"],
  ["no_sid", "No SID"],
  ["no_rek_rdn", "No Rek RDN"],
  ["sub_rekening_efek", "Sub Rekening Efek"],
  ["no_rek_pribadi", "No Rek Pribadi"],
  ["username", "Username"],
  ["no_ktp", "No KTP"],
  ["keterangan", "Keterangan"],
];

export default function DashboardAnggota() {
  const { nama } = useParams();
  const { isAdmin } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(nama || "");
  const [ktpOpen, setKtpOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/anggota").then((r) => {
      setList(r.data);
      if (!nama && r.data.length) setSelected(r.data[0].nama_panggilan);
    }).catch(() => {});
  }, [isAdmin, nama]);

  useEffect(() => {
    const target = isAdmin ? selected : null;
    if (isAdmin && !target) return;
    setLoading(true);
    setError("");
    const url = isAdmin ? `/dashboard/${encodeURIComponent(target)}` : "/me/dashboard";
    api
      .get(url)
      .then((r) => setPayload(r.data))
      .catch((e) => {
        setPayload(null);
        setError(apiError(e));
      })
      .finally(() => setLoading(false));
  }, [isAdmin, selected]);

  useEffect(() => {
    if (nama) setSelected(nama);
  }, [nama]);

  if (loading)
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-[#2b3550]" />
      </div>
    );

  if (error && !payload)
    return (
      <div data-testid="dashboard-page">
        <PageHeader eyebrow="Dashboard Anggota" title="Profil Anggota" />
        <div className="rounded-lg border border-[#1e2536] bg-[#10141d]">
          <EmptyState icon={UserRound} title={error} hint="Hubungi Admin untuk menghubungkan akun Anda dengan data anggota." />
        </div>
      </div>
    );

  const p = payload?.profil || {};
  const r = payload?.ringkasan || {};
  const mutasi = payload?.mutasi || [];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Dashboard Anggota"
        title={p.nama_lengkap || p.nama_panggilan || "Profil"}
        description={`Nama panggilan: ${p.nama_panggilan || "-"} — relasi utama untuk saldo & mutasi.`}
      >
        {isAdmin && list.length > 0 && (
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="dashboard-member-select" className="w-[220px] border-[#1e2536] bg-[#10141d] text-white">
              <SelectValue placeholder="Pilih anggota" />
            </SelectTrigger>
            <SelectContent className="border-[#1e2536] bg-[#10141d] text-white">
              {list.map((a) => (
                <SelectItem key={a.id} value={a.nama_panggilan}>
                  {a.nama_panggilan}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="dash-stat-saldo" label="Saldo Terkini" value={rupiah(r.saldo)} accent="profit" />
        <StatCard testId="dash-stat-masuk" label="Total Dana Masuk" value={rupiah(r.total_masuk)} accent="profit" />
        <StatCard testId="dash-stat-keluar" label="Total Dana Keluar" value={rupiah(r.total_keluar)} accent="loss" />
        <StatCard testId="dash-stat-mutasi" label="Jumlah Mutasi" value={r.jumlah_mutasi ?? 0} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <div className="rounded-lg border border-[#1e2536] bg-[#10141d] p-6" data-testid="dashboard-profile-card">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#00d09c]/40 bg-[#00d09c]/10 font-mono-num text-lg text-[#00d09c]">
              {(p.nama_panggilan || "?").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="text-base font-semibold text-white">{p.nama_panggilan}</p>
              <p className="text-xs text-[#8f9bb3]">{p.sekuritas || "Sekuritas belum diisi"}</p>
              <span
                data-testid="dashboard-status-anggota"
                className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs uppercase tracking-wider ${
                  p.aktif !== false
                    ? "bg-[#00d09c]/10 text-[#00d09c]"
                    : "bg-[#ff3b30]/10 text-[#ff3b30]"
                }`}
              >
                {p.aktif !== false ? "Anggota Aktif" : "Anggota Nonaktif"}
              </span>
            </div>
          </div>
          <div className="mt-6 space-y-3.5">
            {PROFIL_FIELDS.map(([k, label]) => (
              <div key={k} className="flex items-start justify-between gap-4 border-b border-[#1a2030] pb-2.5 last:border-0">
                <span className="text-xs uppercase tracking-[0.12em] text-[#4d586f]">{label}</span>
                <span className="font-mono-num max-w-[60%] break-words text-right text-xs text-white">
                  {k === "no_ktp" && p.link_ktp ? (
                    <button
                      data-testid="dashboard-link-ktp"
                      onClick={() => setKtpOpen(true)}
                      title="Lihat foto KTP"
                      className="inline-flex items-center gap-1 text-[#00d09c] underline-offset-2 transition-colors duration-200 hover:text-[#00e5ff] hover:underline"
                    >
                      {p[k] || "Lihat foto KTP"}
                      <ExternalLink size={11} />
                    </button>
                  ) : (
                    p[k] || "-"
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
          <div className="border-b border-[#1e2536] px-5 py-4">
            <h3 className="text-base text-white">Rincian Mutasi Saldo</h3>
            <p className="mt-1 text-xs text-[#8f9bb3]">Riwayat kredit & debit saldo anggota ini.</p>
          </div>
          {mutasi.length === 0 ? (
            <EmptyState icon={Receipt} title="Belum ada mutasi saldo" hint="Mutasi muncul otomatis saat Admin menyesuaikan saldo." />
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="mutasi-table" className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="border-b border-[#1e2536] bg-[#0d1119] text-left text-xs uppercase tracking-wider text-[#4d586f]">
                    <th className="px-4 py-3 font-medium">Tanggal</th>
                    <th className="px-4 py-3 font-medium">Tipe</th>
                    <th className="px-4 py-3 font-medium">Nominal</th>
                    <th className="px-4 py-3 font-medium">Saldo Akhir</th>
                    <th className="px-4 py-3 font-medium">Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {mutasi.map((m) => (
                    <tr key={m.id} className="border-b border-[#1a2030] last:border-0 hover:bg-white/[0.03]">
                      <td className="font-mono-num whitespace-nowrap px-4 py-3 text-[#8f9bb3]">{tanggal(m.tanggal)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${
                            m.nominal >= 0 ? "bg-[#00d09c]/10 text-[#00d09c]" : "bg-[#ff3b30]/10 text-[#ff3b30]"
                          }`}
                        >
                          {m.nominal >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {m.tipe}
                        </span>
                      </td>
                      <td className={`font-mono-num whitespace-nowrap px-4 py-3 font-semibold ${m.nominal >= 0 ? "text-[#00d09c]" : "text-[#ff3b30]"}`}>
                        {m.nominal >= 0 ? "+" : "-"}
                        {rupiah(Math.abs(m.nominal))}
                      </td>
                      <td className="font-mono-num whitespace-nowrap px-4 py-3 text-white">{rupiah(m.saldo_akhir)}</td>
                      <td className="px-4 py-3 text-[#8f9bb3]">{m.keterangan || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <KtpPreview
        nama={p.nama_panggilan}
        link={p.link_ktp}
        open={ktpOpen}
        onOpenChange={setKtpOpen}
      />
    </div>
  );
}
